import { Tooltip } from 'antd';
import React, {
  FC,
  ReactNode,
  memo,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { Project } from 'api/project';
import { RbacButton } from 'components/organisms/rbac/components/rbac-buttons';
import { RoutePaths } from 'constant';
import { getSelectedTasks } from 'data/sprints/selectors';
import { ProjectResource } from 'hooks/rbac/useWithAccess';
import { useTranslate } from 'hooks/use-translation';
import { logger } from 'services';
import { CreateTaskPopover } from './create-task-popover';
import cx from './index.module.css';
import { withKanbanContext } from './KanbanContext';
import {
  highlightInterval,
  loadingCardCount,
  taskScrollIntersectionThreshold,
} from './knob';
import {
  Task as ITask,
  Id,
  InternalKanbanBoardState,
  KanbanColumns,
  PaginatedSwimlaneColumnFetch,
  TaskCardRendererProps,
  NewTasksDefaultParamsGetterCallbackType,
} from './type';
import { VirtualizedList, VirtualizedListRef } from './VirtualizedList';

interface TaskProps {
  task?: ITask;
  taskCardRenderer: FC<TaskCardRendererProps>;
  isLoading: boolean;
  activeLoading: boolean;
}

const TaskCardRenderer = withKanbanContext<TaskProps>(
  ({
    task,
    setTask,
    taskCardRenderer: ConsumerTaskRenderer,
    isLoading,
    activeLoading,
    readOnly,
  }) => {
    const dragRef = useRef<HTMLDivElement>(null);

    return (
      <>
        <div
          id={`${task?.id}`}
          className={`${cx.taskWrapper}`}
          draggable={!isLoading && !task?.isUpdating && !readOnly}
          onDragStart={(e) => {
            if (!task) {
              return;
            }
            e.persist();
            e.dataTransfer.clearData();
            e.dataTransfer.setData('text/plain', JSON.stringify({ task }));
            if (!dragRef.current) {
              return;
            }
            const taskWrapper = (e.target as any).cloneNode(true);

            if ((e.target as any).style) {
              (e.target as any).style.opacity = 0.5;
            }

            dragRef.current.innerHTML = taskWrapper.innerHTML;
            if (dragRef.current.childNodes[0]) {
              (dragRef.current.childNodes[0] as any).style.transform =
                'rotate(-1deg)';
            }

            e.dataTransfer.setDragImage(dragRef.current, 50, 50);
            dragRef.current.style.display = 'block';
            setTimeout(() => {
              if (!dragRef.current) {
                return;
              }
              logger.log('started to drag', task.id);
              dragRef.current.style.display = 'none';
            }, 0);
            e.dataTransfer.dropEffect = 'move';
            setTask(task!);
          }}
          onDragEnd={(e) => {
            e.preventDefault();
            if (dragRef.current) {
              dragRef.current.innerHTML = '';
            }
            if ((e.target as any).style) {
              (e.target as any).style.opacity = 1;
            }
            e.dataTransfer.clearData();
            setTask(null);
          }}
        >
          <ConsumerTaskRenderer
            id={task?.id ?? ''}
            extra={task?.extra}
            isLoading={isLoading}
            isUpdating={!!task?.isUpdating}
            activeLoading={activeLoading}
            readOnly={readOnly}
          />
        </div>
        <div
          style={{
            position: 'fixed',
            zIndex: 100,
            display: 'none',
            width: '230px',
            transform: 'rotate(-1deg)',
          }}
          className={`${cx.taskWrapper}`}
          ref={dragRef}
        />
      </>
    );
  },
);

interface CounterProps {
  count: number;
  label?: ReactNode;
  className?: string;
}

const Counter: FC<CounterProps> = ({ count, className, label: Label }) => {
  const [meta, setMeta] = useState({ count, highlight: false });

  useLayoutEffect(() => {
    const timerIds: number[] = [];

    timerIds.push(
      window.setTimeout(() => {
        setMeta((oldMeta) => {
          if (oldMeta.count === count) {
            return oldMeta;
          }
          timerIds.push(
            window.setTimeout(() => {
              setMeta({
                count,
                highlight: false,
              });
            }, 1000),
          );
          timerIds.push(
            window.setTimeout(() => {
              setMeta({
                count,
                highlight: true,
              });
            }, 500),
          );

          return {
            count: oldMeta.count,
            highlight: true,
          };
        });
      }, highlightInterval),
    );

    return () => timerIds.forEach((id) => clearTimeout(id));
  }, [count]);

  return (
    <span
      className={`${className} ${cx.counter} ${
        meta.highlight ? cx.highlight : ''
      }`}
    >
      {meta.count} {Label || ''}
    </span>
  );
};

interface ColProps {
  col: KanbanColumns;
  swimlaneId: Id;
  taskCardRenderer: FC<TaskCardRendererProps>;
  taskCardLoadingRenderer: FC<TaskCardRendererProps>;
  paginatedSwimlaneColumnFetch?: PaginatedSwimlaneColumnFetch<any>;
  isFetchingColumn: boolean;
  height: number;
  getNewTaskParams: NewTasksDefaultParamsGetterCallbackType;
  projectDetail?: Project;
}

const Col = withKanbanContext<ColProps>(
  memo(
    ({
      col,
      swimlaneId,
      kanbanActions,
      setTask,
      taskCardRenderer,
      taskCardLoadingRenderer,
      paginatedSwimlaneColumnFetch,
      isFetchingColumn,
      height,
      getNewTaskParams,
      projectDetail,
      readOnly,
    }) => {
      const innerRef = useRef<HTMLDivElement>(null);
      const [isFetching, setIsFetching] = useState(false);
      const ref = useRef<VirtualizedListRef>(null);
      const selectedTasks = useSelector(getSelectedTasks);
      const translate = useTranslate();

      const onScroll = useCallback(
        async (start, end) => {
          if (!paginatedSwimlaneColumnFetch) {
            return;
          }
          const startOffset =
            col.backendPagination.blockSize *
            col.backendPagination.memoryInBlock;
          const endOffset = startOffset + col.backendPagination.blockSize;
          const hasScrolled80Percent =
            end > col.tasks.length * taskScrollIntersectionThreshold;
          const isItemsToBeFetched =
            selectedTasks.length > 0 // In case of sprints flow if there are selected tasks, the tasks count will be greater than offset
              ? true
              : startOffset + 1 >= col.tasks.length;

          if (hasScrolled80Percent && isItemsToBeFetched && !isFetching) {
            logger.debug('trigger fetch inside swimlane', {
              startOffset,
              endOffset,
            });
            setIsFetching(true);
            const data = await paginatedSwimlaneColumnFetch({
              swimlaneId,
              endOffset,
              startOffset,
              columnId: col.id,
            });

            setIsFetching(false);

            kanbanActions.updatePaginatedColumn({
              swimlaneId,
              columnId: col.id,
              tasks: data,
            });
          }
        },
        [
          col.backendPagination.blockSize,
          col.backendPagination.memoryInBlock,
          col.id,
          col.tasks.length,
          kanbanActions,
          paginatedSwimlaneColumnFetch,
          swimlaneId,
          isFetching,
          selectedTasks,
        ],
      );
      const fetchedTaskLength = col.tasks.length + 1;
      const isFetchingExtraCards = fetchedTaskLength <= col.count;
      let loadingItems = 0;

      if (isFetchingExtraCards || isFetchingColumn) {
        loadingItems = Math.min(
          loadingCardCount,
          col.count - fetchedTaskLength + 1,
        );
      } else {
        loadingItems = 0;
      }
      const [isAdding, setIsAdding] = useState(false);
      const { pathname } = useLocation();
      const isSprint = pathname.includes(RoutePaths.sprintPlanning);

      const getRbacRule = () => {
        if (isSprint && projectDetail?.projectId) {
          return 'SPRINT_PROJECT:WRITE';
        }
        if (isSprint) {
          return 'SPRINT:WRITE';
        }

        return 'TASK_ASSIGNEE:WRITE';
      };

      return (
        <div
          className={cx.col}
          style={{ height: `${height}px` }}
          key={col.id}
          onDragOver={(e) => {
            e.preventDefault();
            e.currentTarget.classList.add(cx.colDrag);
          }}
          onDragLeave={(e) => {
            e.currentTarget.classList.remove(cx.colDrag);
          }}
          onDrop={(e) => {
            e.currentTarget.classList.remove(cx.colDrag);
            const { task } = JSON.parse(
              e.dataTransfer.getData('text/plain'),
            ) as { task: ITask };

            e.preventDefault();
            const from = { swimlaneId: task.swimlaneId, colId: task.colId };
            const to = { colId: col.id, swimlaneId };

            kanbanActions.handleDrop({ from, task, to });
            innerRef.current?.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
            setTask(null);
          }}
        >
          <div className={cx.colInner}>
            <div className={cx.colHeader}>
              <Counter count={col.count} />
              <span className={cx.calHeaderLabel}>{col.label}</span>
              {readOnly ? (
                <div className={cx.emptyBtn} />
              ) : (
                <div>
                  <Tooltip title={translate('task.add')} destroyTooltipOnHide>
                    <RbacButton
                      onClick={() => {
                        setIsAdding(true);
                      }}
                      type="text"
                      size="small"
                      rule={getRbacRule()}
                      resourceData={
                        projectDetail
                          ? ProjectResource(projectDetail)
                          : undefined
                      }
                      data-cy={`project.board.statusTile.${col.label.replace(
                        ' ',
                        '',
                      )}.newTask`}
                    >
                      +
                    </RbacButton>
                  </Tooltip>

                  <CreateTaskPopover
                    getNewTaskParams={() =>
                      getNewTaskParams({
                        colValue: col.id,
                        swimlaneValue: swimlaneId,
                      })
                    }
                    visible={isAdding}
                    toggle={() => setIsAdding(false)}
                    placement="bottom"
                  />
                </div>
              )}
            </div>
            <div
              className={cx.tasksContainer}
              style={{
                height: `${height - 45}px`,
              }}
            >
              <VirtualizedList
                innerRef={innerRef}
                ref={ref}
                name="column"
                numItems={col.tasks.length + loadingItems}
                itemHeight={120}
                parentHeight={height - 45}
                onScroll={onScroll}
                renderItem={({ index, style }) => {
                  if (index >= col.tasks.length) {
                    return (
                      <div style={style} key={index}>
                        <TaskCardRenderer
                          task={col.tasks[index]}
                          taskCardRenderer={taskCardLoadingRenderer}
                          isLoading
                          activeLoading={isFetchingColumn || isFetching}
                        />
                      </div>
                    );
                  }

                  return (
                    <div style={style} key={col.tasks[index].id}>
                      <TaskCardRenderer
                        task={col.tasks[index]}
                        taskCardRenderer={taskCardRenderer}
                        isLoading={false}
                        activeLoading={false}
                      />
                    </div>
                  );
                }}
              />
            </div>
          </div>
        </div>
      );
    },
    (pre, cur) => {
      return (
        pre.col.tasks.length === cur.col.tasks.length &&
        pre.swimlaneId === cur.swimlaneId &&
        pre.isFetchingColumn === cur.isFetchingColumn &&
        pre.col.tasks.every((task, index) => {
          return task === cur.col.tasks[index];
        })
      );
    },
  ),
);

interface RowProps {
  swimlaneId: Id;
  kanbanState: InternalKanbanBoardState;
  taskCardRenderer: FC<TaskCardRendererProps<any>>;
  taskCardLoadingRenderer: FC<TaskCardRendererProps>;
  swimlaneColumnFetch?: PaginatedSwimlaneColumnFetch<any>;
  swimlaneHeight: number;
  getNewTaskParams: NewTasksDefaultParamsGetterCallbackType;
  areColumnsFilteredBasedOnStatuses?: boolean;
}

export const Swimlane = withKanbanContext(
  ({
    swimlaneId,
    kanbanState,
    taskCardRenderer,
    swimlaneColumnFetch,
    taskCardLoadingRenderer,
    swimlaneHeight,
    getNewTaskParams,
    projectDetail,
    areColumnsFilteredBasedOnStatuses,
  }: RowProps & { projectDetail?: Project }) => {
    const swimlane = kanbanState.data[swimlaneId];

    const isColumnShown = (id: Id) => {
      return (
        swimlane.cols[id] &&
        (areColumnsFilteredBasedOnStatuses
          ? kanbanState.filteredCols?.includes(id)
          : true)
      );
    };

    return (
      <div
        style={{
          height: `${swimlaneHeight - 40}px`,
        }}
      >
        <div className={cx.rowHeader}>
          <span className={cx.pill}>{swimlane.label}</span>
          <Counter
            className={cx.count}
            count={swimlane.count}
            label={<FormattedMessage id="projects.plan.tasks" />}
          />
        </div>
        <div className={cx.colWrapper}>
          {kanbanState.orderColIds.map((id) => {
            if (isColumnShown(id)) {
              return (
                <Col
                  col={swimlane.cols[id]}
                  key={id}
                  swimlaneId={swimlaneId}
                  taskCardRenderer={taskCardRenderer}
                  paginatedSwimlaneColumnFetch={swimlaneColumnFetch}
                  isFetchingColumn={
                    swimlane.networkState === 'loading' &&
                    swimlane.cols[id].count > 0
                  }
                  taskCardLoadingRenderer={taskCardLoadingRenderer}
                  height={swimlaneHeight - 60}
                  getNewTaskParams={getNewTaskParams}
                  projectDetail={projectDetail}
                />
              );
            }

            return null;
          })}
        </div>
      </div>
    );
  },
);
